<template>
   <div v-if="manager && activeMessageKey">
      <ManagerMessage>
         <h2 class="card-title">{{ header }}</h2>
         <p class="card-description mb-0" v-html="description"></p>
         <div
            class="pt-3 d-flex align-items-center justify-content-end"
            v-if="hasUnlockButton || hasHomeButton"
         >
            <b-button
               v-if="hasUnlockButton"
               class="dark-button mr-2"
               style="font-size: 0.75rem"
               variant="primary"
               @click="unlock"
               >Unlock</b-button
            >
            <b-button
               v-if="hasHomeButton"
               variant="success"
               style="font-size: 0.75rem"
               :to="{name: 'Home'}"
               class="action-button ml-2"
            >
               Dashboard
            </b-button>
         </div>
      </ManagerMessage>
   </div>
</template>

<script>
import {mapGetters} from 'vuex';
import ManagerMessage from '@/components/ManagerMessage.vue';
import {PER_PERIOD_MESSAGE_KEYS, PER_PERIOD_MESSAGE_CONFIG} from '@/helpers/baseConfig';
import {
   getRelevantFlags,
   hasUnresolvedFlags as hasUnresolvedExpenseFlags,
} from '@/helpers/expenseCompletion';
import {managerMessageMixin} from '@/mixins';

export default {
   name: 'PerPeriodMessage',
   components: {
      ManagerMessage,
   },

   mixins: [managerMessageMixin],

   props: {
      expenseType: {
         type: String,
         required: true,
         validator: (value) => ['cloud', 'supplies', 'contractors', 'employees'].includes(value),
      },
      loading: {
         type: Boolean,
         default: false,
      },
   },

   data() {
      return {
         cachedMessageKey: null,
      };
   },

   watch: {
      loading(isLoading) {
         this.cachedMessageKey = isLoading ? this.messageKey : null;
      },
   },

   computed: {
      ...mapGetters({
         flags: 'flags/flags',
         activeStudyId: 'companies/activeStudyId',
      }),
      mergedStatuses() {
         return this.$store.getters[`${this.expenseType}/mergedStatusesByPeriod`](this.periodId);
      },
      crossPeriodMergedStatuses() {
         return this.$store.getters[`${this.expenseType}/crossPeriodMergedStatuses`];
      },

      activeMessageKey() {
         return this.loading && this.cachedMessageKey ? this.cachedMessageKey : this.messageKey;
      },

      currentMessage() {
         return PER_PERIOD_MESSAGE_CONFIG[this.expenseType][this.activeMessageKey];
      },

      header() {
         const ctx = {periodIds: this.periodIds};
         return this.currentMessage.header(ctx);
      },

      description() {
         const ctx = {periodIds: this.periodIds};
         return this.currentMessage.description(ctx);
      },

      noData() {
         return !this.mergedStatusValues.length;
      },

      hasUnresolvedFlags() {
         const relevantFlags = getRelevantFlags({
            flags: this.flags,
            period: this.currentStudyPeriod,
            studyId: this.activeStudyId,
            checkSection: this.flagCheckSection,
            userId: this.profile.id,
         });
         return hasUnresolvedExpenseFlags(relevantFlags, this.$constants().FlagStates);
      },

      mergedStatusValues() {
         return Object.values(this.mergedStatuses);
      },

      hasNonCompleteValues() {
         return (
            this.mergedStatusValues.length > 0 &&
            !this.mergedStatusValues.every((v) => v === 'Complete')
         );
      },

      hasOnlyUnassignedStatus() {
         const keys = Object.keys(this.mergedStatuses);

         if (keys.length !== 1 || keys[0] !== 'null') return false;

         return this.mergedStatusValues[0] !== 'Complete';
      },

      isCrossPeriodUnassignedComplete() {
         return (
            !this.crossPeriodMergedStatuses[null] ||
            this.crossPeriodMergedStatuses[null] === 'Complete'
         );
      },

      isReadyToBeMarkedComplete() {
         return this.areAllAssignmentsComplete && this.isCrossPeriodUnassignedComplete;
      },

      currentStudyPeriod() {
         return this.studyPeriodMap[this.periodId]?.period;
      },

      flagCheckSection() {
         return this.$store.getters[`${this.expenseType}/flagCheckSection`];
      },

      currentPeriodAssignments() {
         const {lower, upper} = this.currentStudyPeriod;
         return this.sectionAssignments.filter(
            ({period}) => period.lower === lower && period.upper === upper
         );
      },

      hasNonCompleteSelfAssignment() {
         return (
            !!this.mergedStatuses[this.profile.id] &&
            this.mergedStatuses[this.profile.id] !== 'Complete'
         );
      },

      hasNonCompleteUnassignedAssignment() {
         return !!this.mergedStatuses[null] && this.mergedStatuses[null] !== 'Complete';
      },

      hasAssignmentsInPeriod() {
         return this.currentPeriodAssignments.some(
            ({assigneeId}) => String(assigneeId ?? null) === String(this.profile.id ?? null)
         );
      },

      myAssignmentsInPeriodAreComplete() {
         const matchingAssignments = this.currentPeriodAssignments.filter(
            ({assigneeId}) => String(assigneeId ?? null) === String(this.profile.id ?? null)
         );

         return (
            matchingAssignments.length > 0 &&
            matchingAssignments.every(({completed}) => !!completed)
         );
      },

      otherAssignmentsInPeriodAreComplete() {
         const others = this.currentPeriodAssignments.filter(
            ({assigneeId}) => assigneeId !== this.profile.id
         );
         return !others.length || others.every(({completed}) => !!completed);
      },

      // Cross-period counterpart of otherAssignmentsInPeriodAreComplete -- that one only
      // catches another assignee who is behind on THIS period. A period can itself be
      // fully 'Complete' while that same assignee is still in progress on a different
      // period, so "ready to mark complete" here must also check every period, not just
      // the current one (mirrors CrossPeriodMessage.vue's otherAssignmentsAreComplete).
      otherAssignmentsAreComplete() {
         const others = this.sectionAssignments.filter(
            ({assigneeId}) => assigneeId !== this.profile.id
         );
         const othersMarkedComplete = !others.length || others.every(({completed}) => !!completed);
         const otherStatusesComplete = Object.entries(this.crossPeriodMergedStatuses)
            .filter(([assigneeId]) => assigneeId !== String(this.profile.id))
            .every(([, status]) => status === 'Complete');

         return othersMarkedComplete && otherStatusesComplete;
      },

      hasNonCompleteOtherAssignment() {
         return Object.entries(this.mergedStatuses).some(
            ([key, status]) =>
               key !== String(this.profile.id) && key !== 'null' && status !== 'Complete'
         );
      },

      smeMessageKey() {
         const {SME_DONE, SME_NEEDS_INPUT, SME_PERIOD_COMPLETE, SME_PERIOD_LOOKS_GOOD} =
            PER_PERIOD_MESSAGE_KEYS;

         if (this.isCompleted) return SME_DONE;
         if (this.hasNonCompleteValues) return SME_NEEDS_INPUT;
         if (this.hasAssignmentsInPeriod && !this.myAssignmentsInPeriodAreComplete) {
            return SME_PERIOD_LOOKS_GOOD;
         }

         return SME_PERIOD_COMPLETE;
      },

      customerMessageKey() {
         const {
            CUSTOMER_ASSIGN,
            CUSTOMER_WAITING_ON_OTHERS,
            CUSTOMER_NEEDS_INPUT,
            CUSTOMER_PERIOD_COMPLETE,
            CUSTOMER_PERIOD_LOOKS_GOOD,
            CUSTOMER_READY_TO_MARK_COMPLETE,
            CUSTOMER_SECTION_COMPLETE,
         } = PER_PERIOD_MESSAGE_KEYS;

         if (this.isCompleted) return CUSTOMER_SECTION_COMPLETE;
         if (this.hasOnlyUnassignedStatus) return CUSTOMER_ASSIGN;
         if (this.hasNonCompleteSelfAssignment) return CUSTOMER_NEEDS_INPUT;
         if (this.hasAssignmentsInPeriod && !this.myAssignmentsInPeriodAreComplete) {
            return CUSTOMER_PERIOD_LOOKS_GOOD;
         }
         if (this.hasNonCompleteOtherAssignment) return CUSTOMER_WAITING_ON_OTHERS;
         if (!this.otherAssignmentsAreComplete) return CUSTOMER_WAITING_ON_OTHERS;
         if (this.isReadyToBeMarkedComplete) return CUSTOMER_READY_TO_MARK_COMPLETE;
         if (!this.otherAssignmentsInPeriodAreComplete || this.hasNonCompleteUnassignedAssignment) {
            return CUSTOMER_WAITING_ON_OTHERS;
         }

         return CUSTOMER_PERIOD_COMPLETE;
      },

      messageKey() {
         const {FLAGS, NO_DATA} = PER_PERIOD_MESSAGE_KEYS;
         if (this.hasUnresolvedFlags) return FLAGS;
         if (this.noData) return NO_DATA;
         if (this.isSME) return this.smeMessageKey;
         if (this.isCustomer) return this.customerMessageKey;

         return null;
      },

      hasHomeButton() {
         const {
            CUSTOMER_SECTION_COMPLETE,
            CUSTOMER_WAITING_ON_OTHERS,
            CUSTOMER_PERIOD_COMPLETE,
            SME_DONE,
            SME_PERIOD_COMPLETE,
         } = PER_PERIOD_MESSAGE_KEYS;
         const messageKeys = [
            CUSTOMER_SECTION_COMPLETE,
            CUSTOMER_WAITING_ON_OTHERS,
            CUSTOMER_PERIOD_COMPLETE,
            SME_PERIOD_COMPLETE,
            SME_DONE,
         ];
         return messageKeys.includes(this.activeMessageKey);
      },

      hasUnlockButton() {
         const {CUSTOMER_SECTION_COMPLETE} = PER_PERIOD_MESSAGE_KEYS;
         return this.activeMessageKey === CUSTOMER_SECTION_COMPLETE;
      },
   },
};
</script>

<style lang="scss" scoped>
.card-title {
   font-size: 1.25rem;
   font-weight: 700;
}

.card-description {
   font-size: 0.8125rem;
}
</style>
